import "./SpacesPage.css";

import { useCallback, useEffect, useMemo, useState } from "react";

import { AddSpacePopup } from "./popups/AddSpacePopup";
import { Button } from "../../../../lib/components/buttons/Button";
import { DetailedCard } from "../../../../lib/components/cards/DetailedCard";
import { PageContainer } from "../../../../lib/layouts/containers/page/PageContainer";
import { PlusSVG } from "../../../../lib/assets/icons";
import { Space } from "../../models/Space";
import { SpaceService } from "../../services/SpaceService";
import { Spacer } from "../../../../lib/components/separator/Spacer";
import { useNavigate } from "react-router-dom";
import { usePopup } from "../../../../lib/infrastructure/ui/UIServices";
import { useServiceCall } from "../../../../common/services/UseServiceCall";
import { IdentityUnavailableState } from "../../../locks/components/LockStates";
import { lockErrorKind } from "../../../locks/services/LockService";

const emptySpaces: Space[] = [];

const service = new SpaceService();

export function SpacesPage() {
  const navigate = useNavigate();
  const openPopup = usePopup();
  const [spaces, setSpaces] = useState<Space[]>(emptySpaces);

  /****************************
   * DATA REQUESTS
   *****************************/
  const getSpaces = useServiceCall(service.getSpaces);

  /****************************
   * DATA MANIPULATION EFFECTS
   *****************************/

  const [identityUnavailable, setIdentityUnavailable] = useState(false);

  // The listing holds only the spaces the caller may read; 503 identity-unavailable means Ark cannot tell which (OrbitalPlan/18 §6).
  const invokeGetSpaces = getSpaces.invoke;
  const loadSpaces = useCallback(() => {
    setIdentityUnavailable(false);
    invokeGetSpaces()
      .then((spaces) => setSpaces(spaces.sort((a,b) => a.title.localeCompare(b.title))))
      .catch((error) => setIdentityUnavailable(lockErrorKind(error) === "identity-unavailable"));
  }, [invokeGetSpaces]);

  useEffect(() => {
    loadSpaces();
    console.log("START", process.env.REACT_APP_INLINE_RUNTIME_CHUNK)
  }, []);

  /****************************
   * USER ACTIONS
   *****************************/

   const handleAddSpaceClicked = useCallback(() => {
    openPopup(<AddSpacePopup onCompleted={() => {getSpaces.invoke().then((spaces) => setSpaces(spaces))}} />)
  }, [openPopup, getSpaces, setSpaces]);


  /****************************
   * CSS & HTML
   *****************************/

    const renderSpacesDetailedCardsHighlighted = useMemo(() => {
    return spaces.filter(s => s.isHighlighted).map((space, idx) => {
      if (space) {
        return (
          <DetailedCard
            key={idx}
            title={space.title}
            description={space.description}
            onClick={() =>
              space.spaceId ? navigate("" +space.spaceId) : null
            }
          />
        );
      }
    });
  }, [spaces]);


  const renderSpacesDetailedCards = useMemo(() => {
    return spaces.map((space, idx) => {
      if (space) {
        return (
          <DetailedCard
            key={idx}
            title={space.title}
            description={space.description}
            onClick={() =>
              space.spaceId ? navigate("" +space.spaceId) : null
            }
          />
        );
      }
    });
  }, [spaces]);

  return (
    <PageContainer className="spaces-page">
      <div className="page-header">
        <h1>Spaces</h1>
        <Button
          className="addspace-button"
          icon={<PlusSVG />}
          text="Add Space"
          type="primary"
          onClick={handleAddSpaceClicked}
        />
      </div>
      {identityUnavailable ? <IdentityUnavailableState onRetry={loadSpaces} /> : null}
      <div className="cards">{renderSpacesDetailedCardsHighlighted}</div>
      <Spacer mode="vertical"/>
      <div className="cards">{renderSpacesDetailedCards}</div>
    </PageContainer>
  );
}
